import { useState } from "react";
import { CalendarDays, CalendarPlus, ChevronLeft, ChevronRight, ExternalLink, MapPin, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { photoSrc, fmtPublished, fmtEvent } from "./newsUtils";
import { CategoryChip, UpcomingBadge } from "./NewsCard";
import { ReminderForm } from "./ReminderForm";

const CalendarButtons = ({ cal }) => {
  const btn = "inline-flex items-center gap-2 border border-navy/20 bg-white px-3 py-2 text-xs font-semibold text-navy transition-colors hover:border-navy hover:bg-mist";
  return (
    <div className="mt-5 border-t border-navy/10 pt-5" data-testid="article-add-to-calendar">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate"><CalendarPlus size={15} className="text-gold-deep" aria-hidden="true" /> Add to calendar</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a className={btn} href={cal.google} target="_blank" rel="noopener noreferrer" data-testid="calendar-google-link">Google Calendar</a>
        <a className={btn} href={photoSrc(cal.ics)} download data-testid="calendar-apple-link">Apple Calendar</a>
        <a className={btn} href={cal.outlook} target="_blank" rel="noopener noreferrer" data-testid="calendar-outlook-link">Outlook.com</a>
        <a className={btn} href={photoSrc(cal.ics)} download data-testid="calendar-ics-link">Outlook desktop / other (.ics)</a>
      </div>
    </div>
  );
};

const EventBox = ({ a }) => (
  <aside className="border-l-4 border-gold bg-white p-6 shadow-[0_4px_20px_rgba(16,38,63,0.06)]" data-testid="article-event-box">
    <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-slate">{a.is_upcoming ? "Event Details" : "This event has taken place"}</p>
    <p className="mt-3 flex items-start gap-2 text-sm font-semibold text-navy"><CalendarDays size={17} className="mt-0.5 shrink-0 text-gold-deep" aria-hidden="true" /> <span data-testid="article-event-date">{fmtEvent(a.event)}</span></p>
    {a.event?.location && <p className="mt-2 flex items-start gap-2 text-sm text-ink"><MapPin size={17} className="mt-0.5 shrink-0 text-gold-deep" aria-hidden="true" /> <span data-testid="article-event-location">{a.event.location}</span></p>}
    {a.is_upcoming && a.event?.link_url && (
      <a href={a.event.link_url} target="_blank" rel="noopener noreferrer" className="btn btn-red mt-5" data-testid="article-event-link">
        {a.event.link_label || "Register"} <ExternalLink size={15} aria-hidden="true" />
      </a>
    )}
    {a.is_upcoming && a.calendar && <CalendarButtons cal={a.calendar} />}
    {a.is_upcoming && a.share_url && <ReminderForm slug={a.slug} />}
  </aside>
);

const Lightbox = ({ photos, index, onClose, onMove }) => {
  const p = photos[index];
  return (
    <Dialog open={index !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl border-none bg-navy p-0 text-paper [&>button]:hidden" data-testid="article-lightbox">
        <DialogTitle className="sr-only">Photo {index + 1} of {photos.length}</DialogTitle>
        {p && (
          <figure>
            <img src={photoSrc(p.url_lg)} alt={p.alt} className="max-h-[78vh] w-full object-contain" />
            <figcaption className="flex items-center justify-between gap-4 p-4 text-sm text-[#D5DEE7]">
              <span>{p.caption}</span>
              <span className="flex shrink-0 gap-2">
                <button type="button" aria-label="Previous photo" onClick={() => onMove(-1)} className="p-2 hover:text-gold" data-testid="lightbox-prev"><ChevronLeft /></button>
                <button type="button" aria-label="Next photo" onClick={() => onMove(1)} className="p-2 hover:text-gold" data-testid="lightbox-next"><ChevronRight /></button>
                <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:text-gold" data-testid="lightbox-close"><X /></button>
              </span>
            </figcaption>
          </figure>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Gallery = ({ photos }) => {
  const [open, setOpen] = useState(null);
  const list = photos.filter(Boolean);
  if (!list.length) return null;
  return (
    <section className="mt-16" data-testid="article-gallery">
      <h2 className="text-2xl font-extrabold text-navy">Photo Gallery</h2>
      <ul className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
        {list.map((p, i) => (
          <li key={`${p.photo}-${i}`}>
            <button type="button" onClick={() => setOpen(i)} className="group block w-full text-left" data-testid={`article-gallery-photo-${i}`}>
              <span className="block aspect-square overflow-hidden bg-mist">
                <img src={photoSrc(p.url_md)} alt={p.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </span>
              {p.caption && <span className="mt-2 block text-xs leading-snug text-slate">{p.caption}</span>}
            </button>
          </li>
        ))}
      </ul>
      <Lightbox photos={list} index={open} onClose={() => setOpen(null)} onMove={(d) => setOpen((i) => (i + d + list.length) % list.length)} />
    </section>
  );
};

export const ArticleView = ({ a, actions = null }) => (
  <article data-testid="article-view">
    <header className="bg-navy pb-12 pt-36 text-paper md:pt-44">
      <div className="container-x">
        <div className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-3">
            <CategoryChip name={a.category?.name} light />
            {a.is_upcoming && <UpcomingBadge testId="article-upcoming-badge" />}
          </div>
          <h1 className="mt-5 text-3xl font-black leading-[1.08] tracking-tight text-paper sm:text-4xl lg:text-5xl" data-testid="article-title">{a.title}</h1>
          {a.summary && <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#D5DEE7] md:text-lg">{a.summary}</p>}
          <p className="mt-6 text-sm text-[#C9D4DF]" data-testid="article-date">{a.publish_at ? `Published ${fmtPublished(a.publish_at)}` : "Not published yet"}</p>
          {actions}
        </div>
      </div>
    </header>
    <div className="container-x py-12 md:py-16">
      <div className="max-w-4xl">
        {a.featured && (
          <figure className="-mt-4 md:-mt-8">
            <img src={photoSrc(a.featured.url_lg)} alt={a.featured.alt} className="aspect-[3/2] w-full object-cover" data-testid="article-featured-photo" />
            {a.featured.caption && <figcaption className="mt-3 text-sm text-slate">{a.featured.caption}</figcaption>}
          </figure>
        )}
        {a.event && <div className="mt-10"><EventBox a={a} /></div>}
        {a.body && <div className="article-body mt-10" dangerouslySetInnerHTML={{ __html: a.body }} data-testid="article-body" />}
        {a.recap && (
          <section className="mt-14 border-t border-navy/10 pt-10" data-testid="article-recap">
            <h2 className="text-2xl font-extrabold text-navy">Event Recap</h2>
            <div className="article-body mt-6" dangerouslySetInnerHTML={{ __html: a.recap }} />
          </section>
        )}
        <Gallery photos={a.gallery ?? []} />
      </div>
    </div>
  </article>
);
